<template>
    <div class="has-tabs">
        <div class="tabs-nav">
            <button
                class="play-toggle"
                type="button"
                :aria-pressed="autoplayEnabled"
                :aria-label="autoplayEnabled
                    ? 'Pause automatic slide rotation'
                    : 'Resume automatic slide rotation'
                "
                @click="toggleAutoplay"
            >
                <transition name="fade" mode="out-in">
                    <svg
                        :key="isPlaying"
                        :class="{ play: !isPlaying }"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path v-if="!isPlaying" d="m22 12-20 11v-22l10 5.5z" />
                        <path v-else d="m2 1v22h7v-22zm13 0v22h7v-22z" />
                    </svg>
                </transition>
            </button>

            <div
                ref="tabContainer"
                class="tabs-nav__list is-horizontal"
                role="tablist"
                aria-label="Select slide to show"
                @keydown.left.prevent="onKeydown(activeTab - 1)"
                @keydown.right.prevent="onKeydown(activeTab + 1)"
                @keydown.home.prevent="onKeydown(0)"
                @keydown.end.prevent="onKeydown(slideItems.length - 1)"
                @mouseenter="onTabsEnter"
                @mouseleave="onTabsLeave"
                @focusin="onTabsFocusIn"
                @focusout="onTabsFocusOut"
                @scroll="updateMask"
            >
                <button
                    v-for="(slide, index) in slideItems"
                    :key="index"
                    ref="tabButtons"
                    type="button"
                    role="tab"
                    :id="`panel-btn-${index}`"
                    :aria-controls="`splide01-slide0${index + 1}`"
                    :aria-selected="(activeTab === index).toString()"
                    :tabindex="activeTab === index ? 0 : -1"
                    @click="selectTab(index, { event: $event, focus: true })"
                >
                    {{ titleException(slide.title) }}
                </button>
            </div>
        </div>

        <section class="tabs-content splide" ref="slider">
            <div class="splide__track">
                <div class="splide__list" ref="tabContent">
                    <CardPanel
                        v-for="(slide, index) in slideItems"
                        :key="index"
                        ref="tabPanels"
                        :class="[
                            'splide__slide tabs-content__panel',
                            { 'is-active': index === activeTab },
                        ]"
                        :slide="slide"
                        :index="index"
                        :activeTab="activeTab"
                    />
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import gsap from 'gsap'
import CardPanel from './CardPanel.vue'

import '@splidejs/splide/dist/css/splide.min.css'
import { Splide } from '@splidejs/splide'
import { Intersection } from '@splidejs/splide-extension-intersection'

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

export default {
    components: {
        CardPanel,
    },

    props: {
        slides: {
            type: Array,
            default: () => [],
        },
    },

    data: () => ({
        autoplayEnabled: true,
        isPlaying: true,
        isInView: true,
        isTabHovered: false,
        isTabFocused: false,
        isReady: false,
        activeTab: 0,
        sliderEl: null,
        scrollToken: 0,
    }),

    computed: {
        slideItems() {
            return this.slides
                .slice(1)
                .sort(() => Math.random() - 0.5)
        },

        sliderConfig() {
            return {
                type: 'loop',
                perPage: 1,
                gap: '2rem',
                speed: 800,
                pagination: false,
                arrows: false,
                autoplay: 'pause',
            }
        },
    },

    watch: {
        activeTab() {
            if (!this.isReady) return

            this.scrollActiveTabIntoView()
        },
        '$store.state.menu.opened'(isOpen) {
            if (!this.isReady) return

            this.sliderAction(!isOpen ? 'play' : 'pause')
        },
    },

    activated() {
        this.isReady = true

        this.sliderInit()
        this.setAutoplay()

        window.addEventListener('resize', this.preventPillTransition)
    },

    deactivated() {
        this.isReady = false

        this.sliderAction('pause')
        window.removeEventListener('resize', this.preventPillTransition)

        clearTimeout(this.resizer)
    },

    methods: {
        titleException(title) {
            // if (title.toLowerCase().includes('career')) {
            //     return 'Career Journey'
            // }

            if (title.toLowerCase().includes('holistic')) {
                return 'Flexibility & Well-being'
            }

            return title
        },

        preventPillTransition() {
            const wrapper = this.$refs.tabContainer

            if (!wrapper || window.matchMedia('(min-width: 992px)').matches) {
                return
            }

            clearTimeout(this.resizer)

            wrapper.style.setProperty('--tr', '0s')

            this.resizer = setTimeout(() => {
                wrapper.style.removeProperty('--tr')
            }, 300)
        },

        updateMask() {
            if (this.ticking) return

            this.ticking = true

            requestAnimationFrame(() => {
                const el = this.$refs.tabContainer
                if (!el) return

                const maxScroll = el.scrollWidth - el.clientWidth
                const value = maxScroll < el.scrollLeft + 40 ? '0rem' : '4rem'

                gsap.to(el.parentElement, {
                    duration: 0.25,
                    '--mask-from-right': value,
                })

                this.ticking = false
            })
        },

        sliderInit() {
            if (this.sliderEl) return

            this.sliderEl = new Splide(this.$refs.slider, this.sliderConfig)

            this.sliderEl.on('move', newIndex => {
                this.activeTab = newIndex
            })

            this.sliderEl.on('autoplay:play', () => {
                this.isPlaying = true
            })

            this.sliderEl.on('autoplay:pause', () => {
                this.isPlaying = false
            })

            this.sliderEl.on('intersection:in', () => {
                this.isInView = true
                this.setAutoplay()
            })

            this.sliderEl.on('intersection:out', () => {
                this.isInView = false
                this.setAutoplay()
            })

            this.sliderEl.mount({ Intersection })
        },

        sliderAction(action = 'play') {
            if (!this.sliderEl) return

            const { Autoplay } = this.sliderEl.Components
            if (!Autoplay || !['play', 'pause'].includes(action)) return

            Autoplay[action]()
        },

        setAutoplay() {
            const shouldPlay =
                this.autoplayEnabled &&
                this.isInView &&
                !this.isTabHovered &&
                !this.isTabFocused

            this.sliderAction(shouldPlay ? 'play' : 'pause')
        },

        toggleAutoplay() {
            this.autoplayEnabled = !this.autoplayEnabled
            this.setAutoplay()
        },

        onTabsEnter() {
            this.isTabHovered = true
            this.setAutoplay()
        },

        onTabsLeave() {
            this.isTabHovered = false
            this.setAutoplay()
        },

        onTabsFocusIn() {
            this.isTabFocused = true
            this.setAutoplay()
        },

        onTabsFocusOut(event) {
            const container = this.$refs.tabContainer

            if (container?.contains(event.relatedTarget)) {
                return
            }

            this.isTabFocused = false
            this.setAutoplay()
        },

        onKeydown(index) {
            if (index < 0) {
                index = this.slideItems.length - 1
            } else if (index >= this.slideItems.length) {
                index = 0
            }

            this.selectTab(index, { focus: true })
        },

        async selectTab(index, options = {}) {
            const { event, focus = false } = options

            if (event) {
                event.preventDefault()
            }

            this.activeTab = index

            await this.$nextTick()

            const button = this.$refs.tabButtons[index]
            if (!button || !this.sliderEl) return

            if (focus) {
                button.focus({ preventScroll: true })
            }

            this.sliderEl.go(index)
            this.scrollActiveTabIntoView()
        },

        async scrollActiveTabIntoView() {
            const token = ++this.scrollToken

            await this.$nextTick()
            await sleep(200)

            if (token !== this.scrollToken) return

            const button = this.$refs.tabButtons[this.activeTab]
            if (!button) return

            button.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.is-horizontal {
    position: relative;
    isolation: isolate;

    width: auto;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding: 0 var(--gutter);
    scroll-behavior: smooth;

    display: flex;
    flex-wrap: nowrap;
    gap: var(--gutter);

    > * {
        scroll-snap-align: start;
    }

    &::-webkit-scrollbar {
        display: none;
    }
}

.tabs-content {
    position: relative;
    overflow: hidden;
    max-width: calc(100vw);
    margin-inline: -20px;

    // pointer-events: none;

    // .is-active {
    //     pointer-events: all;
    // }
}

.splide__track {
    --gutter: 2rem;

    padding-block: var(--splide-padding, 3rem 4rem);
    padding-inline: var(--slide-padding, 60px) !important;
    z-index: 2;

    @media (767px < width < 1280px) {
        --slide-padding: calc(15rem);
    }

    @media (width > 1280px) {
        --splide-padding: 4rem 6rem;
        --slide-padding: calc(20rem);
    }

    @media (width > 1680px) {
        --slide-padding: calc(25%);
    }
}

.tabs-content__panel {
    border-radius: 1rem;

    @media (width > 1280px) {
        // hsl(from var(--purple) h s calc(l - 5))
        --clr: color-mix(in srgb, var(--purple) 15%, #0000);
        --active-offset: 5vmin;
        box-shadow: 0 -.125rem .5rem var(--clr), 0 .95rem 1.75rem var(--clr);
        background: #fff;
        isolation: isolate;
        overflow: hidden;
        top: 0;
        transition: translate .75s var(--timing) .1s;
        margin-top: var(--active-offset);

        &.is-active {
            translate: 0 calc(var(--active-offset) * -1);
        }
    }

    @media (767px < width < 1280px) {
        --active-ofsset: 1em;
    }
}

.tabs-nav button.play-toggle {
    position: absolute;
    top: 50%;
    translate: 0 -50%;
    left: calc(var(--button-size) * -4 + 3 * var(--gutter));
    padding: 0;
    width: calc(var(--button-size) * 4 - 2 * var(--gutter));
    aspect-ratio: 1/1;
    display: grid;
    place-items: center;
    border-radius: 50%;
    --btn-outline-color: var(--purple);
    --outline-offset: calc(var(--gutter) * -1 - 2px);

    &::before,
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        border: 1px solid #0001;
        z-index: -15;
        background: #fff;
    }

    &::after {
        inset: calc(var(--gutter) + 1px);
        transition: background-color .2s;
        border: none;
        z-index: -5;
    }

    svg {
        fill: var(--purple);
        width: 1.125em;
        height: 1.125em;
        display: block;
    }

    svg.play {
        translate: 2px 0;
    }
}

.tabs-nav__list {
    &:has(button:focus-visible) {
        --tr: 0s;
    }

    --tr: .5s;

    button {
        z-index: 1;
    }
}

@media (width < 992px) {
    .tabs-nav {
        --mask-from-left: 0;
        --mask-from-right: 6rem;
    }

    .tabs-nav__list {
        transition: mask .2s;
        mask: linear-gradient(to right, transparent, #000 var(--mask-from-left), #000 calc(100% - var(--mask-from-right)), transparent);
    }
}

.tabs-nav__list::before {
    content: "";
    position: absolute;
    position-anchor: --active;
    left: anchor(--active left);
    right: anchor(--active right);
    top: anchor(--active top);
    bottom: anchor(--active bottom);

    background: var(--theme-color, var(--purple));
    border-radius: inherit;
    transition: inset-inline var(--tr) var(--timing);
    z-index: 0;
}

.tabs-nav {
    font-size: var(--fs-base, .75rem);
    margin: 2rem auto 0;
    border: 1px solid #0001;
    border-radius: 8em;
    max-width: max-content;
    position: relative;
    isolation: isolate;
    margin-inline-start: var(--nav-offset, calc(var(--button-size) * 3));

    --btn-color: #fff;
    --outline-offset: -4px;
    --gutter: 3px;
    --button-size: calc(.85em + var(--gutter));

    &__list {
        background: #fff;
        border-radius: inherit;
        padding: var(--gutter);
    }

    &::after {
        content: '';
        pointer-events: none;
        border-radius: inherit;
        box-shadow: inset 0 0 0 calc(var(--gutter) * 1) #fff;
        position: absolute;
        inset: 0;
        z-index: 2;
    }

    button {
        flex-shrink: 0;
        background: none;
        border: none;
        cursor: pointer;
        border-radius: inherit;
        color: currentColor;
        font-size: inherit;
        padding: .85em 1.25em;
        transition: background-color .2s, color .2s;
    }

    button:hover,
    button:hover::after {
        background-color: #0001;
    }

    button[aria-selected="true"] {
        anchor-name: --active;
        color: #fff;

        @supports not (anchor-name: --active) {
            background: var(--theme-color, var(--purple));
        }
    }

    @media (width > 991px) {
        --gutter: 4px;
        --fs-base: .775rem;
    }

    @media (width > 1144px) {
        --nav-offset: auto;
    }

    @media (width > 1280px) {
        --gutter: 6px;
        --fs-base: .95rem;
    }
}
</style>